import { jsxs, jsx } from "react/jsx-runtime";
import { siteNames, createUrlResolver, getLanguageFromLocale } from "@seek/melways-sites";
import { useTranslations } from "@vocab/react";
import { Box } from "braid-design-system";
import { getAuthenticationStatus, defaultPermissionOptions, EmployerHeaderProvider, DefaultLinkRenderer, isAuthenticated } from "../EmployerFooter/EmployerFooter.mjs";
import { translations } from "../../EmployerHeader/.vocab/index.mjs";
import { BrandedLogo } from "./components/BrandedLogo/BrandedLogo.mjs";
import { CreditHoldAlert } from "./components/CreditHoldAlert/CreditHoldAlert.mjs";
import { HeaderActions } from "./components/HeaderActions/HeaderActions.mjs";
import { ImpersonationBanner } from "./components/ImpersonationBanner/ImpersonationBanner.mjs";
import { MobileMenuDropdown } from "./components/MobileMenuDropdown/MobileMenuDropdown.mjs";
import { DesktopNavigation } from "./components/Navigation/DesktopNavigation.mjs";
import { DesktopSubNavigation } from "./components/Navigation/DesktopSubNavigation.mjs";
import { ScreenReaderSkipLink } from "./components/ScreenReaderHelpers/ScreenReaderSkipLink.mjs";
import { ScreenReaderSkipTarget } from "./components/ScreenReaderHelpers/ScreenReaderSkipTarget.mjs";
import * as pageUrl from "@seek/adv-constants";
import { analyticsEvents, analyticsDesktopHeaderEvents, filterLinksOnSiteAndHref } from "../EmployerFooter/AuthenticatedFooterLinks/AuthenticatedFooterLinks.mjs";
import { root, content, actionsContent } from "./EmployerHeader.css.mjs";
const testTags = {
  // Header menu items
  HOME: "home-page",
  JOBS: "jobs-page",
  PRODUCTS: "products-page",
  HIRING_ADVICE: "hiring-advice",
  MARKET_INSIGHTS: "market-insights",
  ANALYTICS: "analytics",
  // Jobs sub menu items
  JOBS_OPEN: "jobs-open-page",
  JOBS_EXPIRED: "jobs-expired-page",
  JOBS_DRAFT: "jobs-draft-page",
  JOBS_MANAGE_CANDIDATES: "jobs-manage-candidates-page",
  // Products sub menu items
  PRODUCTS_HUB: "products-hub",
  PRODUCTS_JOB_ADS: "job-ads-page",
  PRODUCTS_DISCOUNTS: "discounts-page",
  PRODUCTS_CANDIDATE_MANAGEMENT: "candidate-management-page",
  PRODUCTS_TALENT_SEARCH: "talent-search-page",
  PRODUCTS_BRANDING: "branding-page",
  PRODUCTS_COMPANY_REVIEWS: "company-reviews-page",
  PRODUCTS_COMPANY_PROFILES: "company-profiles-page",
  // Hiring Advice sub menu items
  HIRING_ADVICE_AD_WRITING: "ad-writing-page",
  HIRING_ADVICE_TALENT_ACQUISITION: "talent-acquisition-page",
  HIRING_ADVICE_SCREENING_CANDIDATES: "screening-candidates-page",
  HIRING_ADVICE_INTERVIEWS: "interviews-page",
  HIRING_ADVICE_LEGAL: "legal-page",
  HIRING_ADVICE_ATTRACTING_CANDIDATES: "attracting-candidates-page",
  HIRING_ADVICE_HIRING_PROCESS: "hiring-process-page",
  HIRING_ADVICE_ENGAGING_WORKFORCE: "engaging-workforce-page",
  // Market insights sub menu items
  MARKET_INSIGHTS_EMPLOYMENT_TRENDS: "employment-trends-page",
  MARKET_INSIGHTS_LAWS_OF_ATTRACTION: "laws-of-attraction-page",
  MARKET_INSIGHTS_TALENT_TALKS: "talent-talks-page",
  MARKET_INSIGHTS_FUTURE_OF_WORK: "future-of-work-page",
  MARKET_INSIGHTS_SALARY_TRENDS: "salary-trends-page",
  MARKET_INSIGHTS_CANDIDATE_INSIGHTS: "candidate-insights-page",
  MARKET_INSIGHTS_SEEK_EMPLOYMENT_TRENDS: "seek-employment-trends-page",
  MARKET_INSIGHTS_WORKFORCE_INSIGHTS: "workforce-insights-page",
  MARKET_INSIGHTS_REPORTS: "reports-page",
  // Logged In Account Dropdowns
  SWITCH_ACCOUNT: "switch-account-page",
  ACCOUNT_DETAILS: "account-details-page",
  YOUR_TEAM: "users-page",
  PAYABLE_INVOICES: "payable-invoices-page",
  INVOICE_HISTORY: "invoice-history-page",
  COMPANY_PROFILE: "company-profile-page",
  LOGOS: "logos-page",
  AD_PRICE_LOOKUP: "ad-price-lookup-page",
  CONTACT_US: "contact-us-page",
  SIGN_OUT: "sign-out-page",
  // Sign in and registation
  SIGN_IN: "sign-in-page",
  REGISTER: "register-page",
  CREATE_A_JOB_AD: "create-a-job-ad-button",
  LOGO: "seek-logo"
};
const getFilteredHeaderNavLinks = (rawNavItems, site) => {
  const filteredNavItems = rawNavItems.filter(
    (item) => filterLinksOnSiteAndHref(item, site)
  );
  return filteredNavItems.map((item) => {
    if (item.subItems && item.subItems.length > 0) {
      return {
        ...item,
        subItems: item.subItems.filter(
          (subItem) => filterLinksOnSiteAndHref(subItem, site)
        )
      };
    }
    return item;
  });
};
const useHeaderNavItems = (site, urlResolver) => {
  const { t } = useTranslations(translations);
  const authenticatedNavItems = [
    {
      id: "home",
      label: t("Home"),
      href: urlResolver({
        path: pageUrl.URL_DASHBOARD
      }),
      "data-analytics": analyticsEvents.TRACK_HOME,
      "data-test": testTags.HOME,
      analyticsEvent: analyticsDesktopHeaderEvents.home
    },
    {
      id: "jobs",
      label: t("Jobs"),
      href: urlResolver({
        path: pageUrl.URL_LIST_JOBS_OPEN
      }),
      "data-analytics": analyticsEvents.TRACK_LIST_JOBS,
      "data-test": testTags.JOBS,
      analyticsEvent: analyticsDesktopHeaderEvents.jobs,
      rel: "nofollow",
      subItems: [
        {
          id: "open",
          label: t("Open"),
          href: urlResolver({
            path: pageUrl.URL_LIST_JOBS_OPEN
          }),
          "data-analytics": analyticsEvents.TRACK_LIST_JOBS_OPEN,
          "data-test": testTags.JOBS_OPEN,
          analyticsEvent: analyticsDesktopHeaderEvents.jobsOpen,
          rel: "nofollow"
        },
        {
          id: "expired",
          label: t("Expired"),
          href: urlResolver({
            path: pageUrl.URL_LIST_JOBS_EXPIRED
          }),
          "data-analytics": analyticsEvents.TRACK_LIST_JOBS_EXPIRED,
          "data-test": testTags.JOBS_EXPIRED,
          analyticsEvent: analyticsDesktopHeaderEvents.jobsExpired,
          rel: "nofollow"
        },
        {
          id: "draft",
          label: t("Draft"),
          href: urlResolver({
            path: pageUrl.URL_LIST_JOBS_DRAFT
          }),
          "data-analytics": analyticsEvents.TRACK_LIST_JOBS_DRAFT,
          "data-test": testTags.JOBS_DRAFT,
          analyticsEvent: analyticsDesktopHeaderEvents.jobsDraft,
          rel: "nofollow"
        },
        {
          id: "candidates",
          label: t("Candidates"),
          href: urlResolver({
            path: pageUrl.URL_YOUR_CANDIDATES
          }),
          "data-analytics": analyticsEvents.TRACK_MANAGE_CANDIDATES,
          "data-test": testTags.JOBS_MANAGE_CANDIDATES,
          analyticsEvent: analyticsDesktopHeaderEvents.jobsYourCandidates,
          rel: "nofollow"
        }
      ]
    },
    {
      id: "products",
      label: t("Products"),
      href: urlResolver({
        path: pageUrl.URL_PRODUCTS_AD_BUDGETS
      }),
      "data-analytics": analyticsEvents.TRACK_PRODUCTS,
      "data-test": testTags.PRODUCTS,
      analyticsEvent: analyticsDesktopHeaderEvents.products,
      subItems: [
        {
          id: "whatsNew",
          label: t("What’s new"),
          href: urlResolver({
            path: pageUrl.URL_PRODUCTS_HUB
          }),
          "data-analytics": analyticsEvents.TRACK_PRODUCTS_HUB,
          "data-test": testTags.PRODUCTS_HUB,
          analyticsEvent: analyticsDesktopHeaderEvents.productsHubWhatsNew
        },
        {
          id: "jobAds",
          label: t("Job ads"),
          href: urlResolver({
            path: pageUrl.URL_PRODUCTS_JOB_ADS
          }),
          "data-analytics": analyticsEvents.TRACK_JOB_ADS_PRODUCT,
          "data-test": testTags.PRODUCTS_JOB_ADS,
          analyticsEvent: analyticsDesktopHeaderEvents.productsJobAds
        },
        {
          id: "discounts",
          label: t("Discounts"),
          href: urlResolver({
            path: pageUrl.URL_PRODUCTS_AD_BUDGETS
          }),
          "data-analytics": analyticsEvents.TRACK_DISCOUNTS_PRODUCT,
          "data-test": testTags.PRODUCTS_DISCOUNTS,
          analyticsEvent: analyticsDesktopHeaderEvents.productsDiscounts
        },
        {
          id: "candidateManagement",
          label: t("Candidate management"),
          href: urlResolver({
            path: pageUrl.URL_PRODUCTS_CANDIDATE_MANAGEMENT
          }),
          "data-analytics": analyticsEvents.TRACK_CANDIDATE_MANAGEMENT_PRODUCT,
          "data-test": testTags.PRODUCTS_CANDIDATE_MANAGEMENT,
          analyticsEvent: analyticsDesktopHeaderEvents.productsCandidateManagement
        },
        {
          id: "talentSearch",
          label: t("Talent search"),
          href: urlResolver({
            path: pageUrl.URL_PRODUCTS_TALENT_SEARCH
          }),
          "data-analytics": analyticsEvents.TRACK_TALENT_SEARCH_PRODUCT,
          "data-test": testTags.PRODUCTS_TALENT_SEARCH,
          analyticsEvent: analyticsDesktopHeaderEvents.productsTalentSearch
        },
        {
          id: "branding",
          label: t("Branding"),
          href: urlResolver({
            path: pageUrl.URL_PRODUCTS_BRANDING
          }),
          "data-analytics": analyticsEvents.TRACK_BRANDING_PRODUCT,
          "data-test": testTags.PRODUCTS_BRANDING,
          analyticsEvent: analyticsDesktopHeaderEvents.productsBranding
        },
        {
          id: "companyProfiles",
          label: t("Company profiles"),
          href: urlResolver({
            path: pageUrl.URL_PRODUCTS_COMPANY_PROFILES
          }),
          "data-analytics": analyticsEvents.TRACK_COMPANY_PROFILES_PRODUCT,
          "data-test": testTags.PRODUCTS_COMPANY_PROFILES,
          analyticsEvent: analyticsDesktopHeaderEvents.productsCompanyProfiles
        }
      ]
    },
    {
      id: "hiringAdvice",
      label: t("Hiring Advice"),
      href: urlResolver({
        path: pageUrl.URL_BLOG_HIRING_ADVICE_ROOT
      }),
      "data-analytics": analyticsEvents.TRACK_BLOG_HIRING_ADVICE,
      "data-test": testTags.HIRING_ADVICE,
      analyticsEvent: analyticsDesktopHeaderEvents.hiringAdvice,
      subItems: [
        {
          id: "attractingCandidates",
          label: t("Attracting candidates"),
          href: urlResolver({
            path: pageUrl.URL_BLOG_HIRING_ADVICE_CATEGORY_ATTRACTING_CANDIDATES
          }),
          "data-analytics": analyticsEvents.TRACK_BLOG_CATEGORY_ATTRACTING_CANDIDATES,
          "data-test": testTags.HIRING_ADVICE_ATTRACTING_CANDIDATES,
          analyticsEvent: analyticsDesktopHeaderEvents.hiringAdviceAttractingCandidates,
          specificSite: [
            siteNames.employer.seek.hk,
            siteNames.employer.seek.id,
            siteNames.employer.seek.my,
            siteNames.employer.seek.ph,
            siteNames.employer.seek.sg,
            siteNames.employer.seek.th
          ]
        },
        {
          id: "hiringProcess",
          label: t("Hiring process"),
          href: urlResolver({
            path: pageUrl.URL_BLOG_HIRING_ADVICE_CATEGORY_HIRING_PROCESS
          }),
          "data-analytics": analyticsEvents.TRACK_BLOG_CATEGORY_HIRING_PROCESS,
          "data-test": testTags.HIRING_ADVICE_HIRING_PROCESS,
          analyticsEvent: analyticsDesktopHeaderEvents.hiringAdviceHiringProcess,
          specificSite: [
            siteNames.employer.seek.hk,
            siteNames.employer.seek.id,
            siteNames.employer.seek.my,
            siteNames.employer.seek.ph,
            siteNames.employer.seek.sg,
            siteNames.employer.seek.th
          ]
        },
        {
          id: "engagingYourWorkforce",
          label: t("Engaging your workforce"),
          href: urlResolver({
            path: pageUrl.URL_BLOG_HIRING_ADVICE_CATEGORY_ENGAGING_WORKFORCE
          }),
          "data-analytics": analyticsEvents.TRACK_BLOG_CATEGORY_ENGAGING_WORKFORCE,
          "data-test": testTags.HIRING_ADVICE_ENGAGING_WORKFORCE,
          analyticsEvent: analyticsDesktopHeaderEvents.hiringAdviceEngagingYourWorkforce,
          specificSite: [
            siteNames.employer.seek.hk,
            siteNames.employer.seek.id,
            siteNames.employer.seek.my,
            siteNames.employer.seek.ph,
            siteNames.employer.seek.sg,
            siteNames.employer.seek.th
          ]
        }
      ]
    },
    {
      id: "marketInsights",
      label: t("Market Insights"),
      href: urlResolver({
        path: pageUrl.URL_BLOG_MARKET_INSIGHTS_ROOT
      }),
      "data-analytics": analyticsEvents.TRACK_BLOG_MARKET_INSIGHTS,
      "data-test": testTags.MARKET_INSIGHTS,
      analyticsEvent: analyticsDesktopHeaderEvents.marketInsights,
      subItems: [
        {
          id: "seekEmploymentTrends",
          label: t("SEEK Employment Trends"),
          href: urlResolver({
            path: pageUrl.URL_BLOG_MARKET_INSIGHTS_CATEGORY_SEEK_EMPLOYMENT_TRENDS
          }),
          "data-analytics": analyticsEvents.TRACK_BLOG_EMPLOYMENT_TRENDS,
          "data-test": testTags.MARKET_INSIGHTS_EMPLOYMENT_TRENDS,
          analyticsEvent: analyticsDesktopHeaderEvents.marketInsightsEmploymentTrends,
          specificSite: [
            siteNames.employer.seek.hk,
            siteNames.employer.seek.id,
            siteNames.employer.seek.my,
            siteNames.employer.seek.ph,
            siteNames.employer.seek.sg,
            siteNames.employer.seek.th
          ]
        },
        {
          id: "salaryTrends",
          label: t("Salary trends"),
          href: urlResolver({
            path: pageUrl.URL_BLOG_MARKET_INSIGHTS_CATEGORY_SALARY_TRENDS
          }),
          "data-analytics": analyticsEvents.TRACK_BLOG_SUB_SALARY_TRENDS,
          "data-test": testTags.MARKET_INSIGHTS_SALARY_TRENDS,
          analyticsEvent: analyticsDesktopHeaderEvents.marketInsightsSalaryTrends,
          specificSite: [
            siteNames.employer.seek.hk,
            siteNames.employer.seek.id,
            siteNames.employer.seek.my,
            siteNames.employer.seek.ph,
            siteNames.employer.seek.sg,
            siteNames.employer.seek.th
          ]
        },
        {
          id: "candidateInsights",
          label: t("Candidate insights"),
          href: urlResolver({
            path: pageUrl.URL_BLOG_MARKET_INSIGHTS_CATEGORY_CANDIDATE_INSIGHTS
          }),
          "data-analytics": analyticsEvents.TRACK_BLOG_CANDIDATE_INSIGHTS,
          "data-test": testTags.MARKET_INSIGHTS_CANDIDATE_INSIGHTS,
          analyticsEvent: analyticsDesktopHeaderEvents.marketInsightsCandidateInsights,
          specificSite: [
            siteNames.employer.seek.hk,
            siteNames.employer.seek.id,
            siteNames.employer.seek.my,
            siteNames.employer.seek.ph,
            siteNames.employer.seek.sg,
            siteNames.employer.seek.th
          ]
        },
        {
          id: "workforceInsights",
          label: t("Workforce insights"),
          href: urlResolver({
            path: pageUrl.URL_BLOG_MARKET_INSIGHTS_CATEGORY_WORKFORCE_INSIGHTS
          }),
          "data-analytics": analyticsEvents.TRACK_BLOG_WORKFORCE_INSIGHTS,
          "data-test": testTags.MARKET_INSIGHTS_WORKFORCE_INSIGHTS,
          analyticsEvent: analyticsDesktopHeaderEvents.marketInsightsWorkforceInsights,
          specificSite: [
            siteNames.employer.seek.hk,
            siteNames.employer.seek.id,
            siteNames.employer.seek.my,
            siteNames.employer.seek.ph,
            siteNames.employer.seek.sg,
            siteNames.employer.seek.th
          ]
        },
        {
          id: "reports",
          label: t("Reports"),
          href: urlResolver({
            path: pageUrl.URL_BLOG_MARKET_INSIGHTS_CATEGORY_REPORTS
          }),
          "data-analytics": analyticsEvents.TRACK_BLOG_REPORTS,
          "data-test": testTags.MARKET_INSIGHTS_REPORTS,
          analyticsEvent: analyticsDesktopHeaderEvents.marketInsightsReports,
          specificSite: [
            siteNames.employer.seek.hk,
            siteNames.employer.seek.id,
            siteNames.employer.seek.my,
            siteNames.employer.seek.ph,
            siteNames.employer.seek.sg,
            siteNames.employer.seek.th
          ]
        }
      ]
    },
    {
      id: "analytics",
      label: t("Analytics"),
      href: urlResolver({
        path: pageUrl.URL_ANALYTICS
      }),
      "data-analytics": analyticsEvents.TRACK_ANALYTICS,
      "data-test": testTags.ANALYTICS,
      analyticsEvent: analyticsDesktopHeaderEvents.analytics
    }
  ];
  const unauthenticatedNavItems = [
    {
      id: "home",
      label: t("Home"),
      href: urlResolver({
        path: pageUrl.URL_ROOT
      }),
      "data-analytics": analyticsEvents.TRACK_HOME,
      "data-test": testTags.HOME,
      analyticsEvent: analyticsDesktopHeaderEvents.home
    },
    {
      id: "jobs",
      label: t("Jobs"),
      href: urlResolver({
        path: pageUrl.URL_LIST_JOBS_OPEN
      }),
      "data-analytics": analyticsEvents.TRACK_LIST_JOBS,
      "data-test": testTags.JOBS,
      analyticsEvent: analyticsDesktopHeaderEvents.jobs,
      rel: "nofollow",
      subItems: [
        {
          id: "open",
          label: t("Open"),
          href: urlResolver({
            path: pageUrl.URL_LIST_JOBS_OPEN
          }),
          "data-analytics": analyticsEvents.TRACK_LIST_JOBS_OPEN,
          "data-test": testTags.JOBS_OPEN,
          analyticsEvent: analyticsDesktopHeaderEvents.jobsOpen,
          rel: "nofollow"
        },
        {
          id: "expired",
          label: t("Expired"),
          href: urlResolver({
            path: pageUrl.URL_LIST_JOBS_EXPIRED
          }),
          "data-analytics": analyticsEvents.TRACK_LIST_JOBS_EXPIRED,
          "data-test": testTags.JOBS_EXPIRED,
          analyticsEvent: analyticsDesktopHeaderEvents.jobsExpired,
          rel: "nofollow"
        },
        {
          id: "draft",
          label: t("Draft"),
          href: urlResolver({
            path: pageUrl.URL_LIST_JOBS_DRAFT
          }),
          "data-analytics": analyticsEvents.TRACK_LIST_JOBS_DRAFT,
          "data-test": testTags.JOBS_DRAFT,
          analyticsEvent: analyticsDesktopHeaderEvents.jobsDraft,
          rel: "nofollow"
        },
        {
          id: "candidates",
          label: t("Candidates"),
          href: urlResolver({
            path: pageUrl.URL_YOUR_CANDIDATES
          }),
          "data-analytics": analyticsEvents.TRACK_MANAGE_CANDIDATES,
          "data-test": testTags.JOBS_MANAGE_CANDIDATES,
          analyticsEvent: analyticsDesktopHeaderEvents.jobsYourCandidates,
          rel: "nofollow"
        }
      ]
    },
    {
      id: "products",
      label: t("Products"),
      href: urlResolver({
        path: pageUrl.URL_PRODUCTS_AD_BUDGETS
      }),
      "data-analytics": analyticsEvents.TRACK_PRODUCTS,
      "data-test": testTags.PRODUCTS,
      analyticsEvent: analyticsDesktopHeaderEvents.products,
      subItems: [
        {
          id: "whatsNew",
          label: t("What’s new"),
          href: urlResolver({
            path: pageUrl.URL_PRODUCTS_HUB
          }),
          "data-analytics": analyticsEvents.TRACK_PRODUCTS_HUB,
          "data-test": testTags.PRODUCTS_HUB,
          analyticsEvent: analyticsDesktopHeaderEvents.productsHubWhatsNew
        },
        {
          id: "jobAds",
          label: t("Job ads"),
          href: urlResolver({
            path: pageUrl.URL_PRODUCTS_JOB_ADS
          }),
          "data-analytics": analyticsEvents.TRACK_JOB_ADS_PRODUCT,
          "data-test": testTags.PRODUCTS_JOB_ADS,
          analyticsEvent: analyticsDesktopHeaderEvents.productsJobAds
        },
        {
          id: "discounts",
          label: t("Discounts"),
          href: urlResolver({
            path: pageUrl.URL_PRODUCTS_AD_BUDGETS
          }),
          "data-analytics": analyticsEvents.TRACK_DISCOUNTS_PRODUCT,
          "data-test": testTags.PRODUCTS_DISCOUNTS,
          analyticsEvent: analyticsDesktopHeaderEvents.productsDiscounts
        },
        {
          id: "candidateManagement",
          label: t("Candidate management"),
          href: urlResolver({
            path: pageUrl.URL_PRODUCTS_CANDIDATE_MANAGEMENT
          }),
          "data-analytics": analyticsEvents.TRACK_CANDIDATE_MANAGEMENT_PRODUCT,
          "data-test": testTags.PRODUCTS_CANDIDATE_MANAGEMENT,
          analyticsEvent: analyticsDesktopHeaderEvents.productsCandidateManagement
        },
        {
          id: "talentSearch",
          label: t("Talent search"),
          href: urlResolver({
            path: pageUrl.URL_PRODUCTS_TALENT_SEARCH
          }),
          "data-analytics": analyticsEvents.TRACK_TALENT_SEARCH_PRODUCT,
          "data-test": testTags.PRODUCTS_TALENT_SEARCH,
          analyticsEvent: analyticsDesktopHeaderEvents.productsTalentSearch
        },
        {
          id: "branding",
          label: t("Branding"),
          href: urlResolver({
            path: pageUrl.URL_PRODUCTS_BRANDING
          }),
          "data-analytics": analyticsEvents.TRACK_BRANDING_PRODUCT,
          "data-test": testTags.PRODUCTS_BRANDING,
          analyticsEvent: analyticsDesktopHeaderEvents.productsBranding
        },
        {
          id: "companyProfiles",
          label: t("Company profiles"),
          href: urlResolver({
            path: pageUrl.URL_PRODUCTS_COMPANY_PROFILES
          }),
          "data-analytics": analyticsEvents.TRACK_COMPANY_PROFILES_PRODUCT,
          "data-test": testTags.PRODUCTS_COMPANY_PROFILES,
          analyticsEvent: analyticsDesktopHeaderEvents.productsCompanyProfiles
        }
      ]
    },
    {
      id: "hiringAdvice",
      label: t("Hiring Advice"),
      href: urlResolver({
        path: pageUrl.URL_BLOG_HIRING_ADVICE_ROOT
      }),
      "data-analytics": analyticsEvents.TRACK_BLOG_HIRING_ADVICE,
      "data-test": testTags.HIRING_ADVICE,
      analyticsEvent: analyticsDesktopHeaderEvents.hiringAdvice,
      subItems: [
        {
          id: "attractingCandidates",
          label: t("Attracting candidates"),
          href: urlResolver({
            path: pageUrl.URL_BLOG_HIRING_ADVICE_CATEGORY_ATTRACTING_CANDIDATES
          }),
          "data-analytics": analyticsEvents.TRACK_BLOG_CATEGORY_ATTRACTING_CANDIDATES,
          "data-test": testTags.HIRING_ADVICE_ATTRACTING_CANDIDATES,
          analyticsEvent: analyticsDesktopHeaderEvents.hiringAdviceAttractingCandidates,
          specificSite: [
            siteNames.employer.seek.hk,
            siteNames.employer.seek.id,
            siteNames.employer.seek.my,
            siteNames.employer.seek.ph,
            siteNames.employer.seek.sg,
            siteNames.employer.seek.th
          ]
        },
        {
          id: "hiringProcess",
          label: t("Hiring process"),
          href: urlResolver({
            path: pageUrl.URL_BLOG_HIRING_ADVICE_CATEGORY_HIRING_PROCESS
          }),
          "data-analytics": analyticsEvents.TRACK_BLOG_CATEGORY_HIRING_PROCESS,
          "data-test": testTags.HIRING_ADVICE_HIRING_PROCESS,
          analyticsEvent: analyticsDesktopHeaderEvents.hiringAdviceHiringProcess,
          specificSite: [
            siteNames.employer.seek.hk,
            siteNames.employer.seek.id,
            siteNames.employer.seek.my,
            siteNames.employer.seek.ph,
            siteNames.employer.seek.sg,
            siteNames.employer.seek.th
          ]
        },
        {
          id: "engagingYourWorkforce",
          label: t("Engaging your workforce"),
          href: urlResolver({
            path: pageUrl.URL_BLOG_HIRING_ADVICE_CATEGORY_ENGAGING_WORKFORCE
          }),
          "data-analytics": analyticsEvents.TRACK_BLOG_CATEGORY_ENGAGING_WORKFORCE,
          "data-test": testTags.HIRING_ADVICE_ENGAGING_WORKFORCE,
          analyticsEvent: analyticsDesktopHeaderEvents.hiringAdviceEngagingYourWorkforce,
          specificSite: [
            siteNames.employer.seek.hk,
            siteNames.employer.seek.id,
            siteNames.employer.seek.my,
            siteNames.employer.seek.ph,
            siteNames.employer.seek.sg,
            siteNames.employer.seek.th
          ]
        }
      ]
    },
    {
      id: "marketInsights",
      label: t("Market Insights"),
      href: urlResolver({
        path: pageUrl.URL_BLOG_MARKET_INSIGHTS_ROOT
      }),
      "data-analytics": analyticsEvents.TRACK_BLOG_MARKET_INSIGHTS,
      "data-test": testTags.MARKET_INSIGHTS,
      analyticsEvent: analyticsDesktopHeaderEvents.marketInsights,
      subItems: [
        {
          id: "seekEmploymentTrends",
          label: t("SEEK Employment Trends"),
          href: urlResolver({
            path: pageUrl.URL_BLOG_MARKET_INSIGHTS_CATEGORY_SEEK_EMPLOYMENT_TRENDS
          }),
          "data-analytics": analyticsEvents.TRACK_BLOG_EMPLOYMENT_TRENDS,
          "data-test": testTags.MARKET_INSIGHTS_EMPLOYMENT_TRENDS,
          analyticsEvent: analyticsDesktopHeaderEvents.marketInsightsEmploymentTrends,
          specificSite: [
            siteNames.employer.seek.hk,
            siteNames.employer.seek.id,
            siteNames.employer.seek.my,
            siteNames.employer.seek.ph,
            siteNames.employer.seek.sg,
            siteNames.employer.seek.th
          ]
        },
        {
          id: "salaryTrends",
          label: t("Salary trends"),
          href: urlResolver({
            path: pageUrl.URL_BLOG_MARKET_INSIGHTS_CATEGORY_SALARY_TRENDS
          }),
          "data-analytics": analyticsEvents.TRACK_BLOG_SUB_SALARY_TRENDS,
          "data-test": testTags.MARKET_INSIGHTS_SALARY_TRENDS,
          analyticsEvent: analyticsDesktopHeaderEvents.marketInsightsSalaryTrends,
          specificSite: [
            siteNames.employer.seek.hk,
            siteNames.employer.seek.id,
            siteNames.employer.seek.my,
            siteNames.employer.seek.ph,
            siteNames.employer.seek.sg,
            siteNames.employer.seek.th
          ]
        },
        {
          id: "candidateInsights",
          label: t("Candidate insights"),
          href: urlResolver({
            path: pageUrl.URL_BLOG_MARKET_INSIGHTS_CATEGORY_CANDIDATE_INSIGHTS
          }),
          "data-analytics": analyticsEvents.TRACK_BLOG_CANDIDATE_INSIGHTS,
          "data-test": testTags.MARKET_INSIGHTS_CANDIDATE_INSIGHTS,
          analyticsEvent: analyticsDesktopHeaderEvents.marketInsightsCandidateInsights,
          specificSite: [
            siteNames.employer.seek.hk,
            siteNames.employer.seek.id,
            siteNames.employer.seek.my,
            siteNames.employer.seek.ph,
            siteNames.employer.seek.sg,
            siteNames.employer.seek.th
          ]
        },
        {
          id: "workforceInsights",
          label: t("Workforce insights"),
          href: urlResolver({
            path: pageUrl.URL_BLOG_MARKET_INSIGHTS_CATEGORY_WORKFORCE_INSIGHTS
          }),
          "data-analytics": analyticsEvents.TRACK_BLOG_WORKFORCE_INSIGHTS,
          "data-test": testTags.MARKET_INSIGHTS_WORKFORCE_INSIGHTS,
          analyticsEvent: analyticsDesktopHeaderEvents.marketInsightsWorkforceInsights,
          specificSite: [
            siteNames.employer.seek.hk,
            siteNames.employer.seek.id,
            siteNames.employer.seek.my,
            siteNames.employer.seek.ph,
            siteNames.employer.seek.sg,
            siteNames.employer.seek.th
          ]
        },
        {
          id: "reports",
          label: t("Reports"),
          href: urlResolver({
            path: pageUrl.URL_BLOG_MARKET_INSIGHTS_CATEGORY_REPORTS
          }),
          "data-analytics": analyticsEvents.TRACK_BLOG_REPORTS,
          "data-test": testTags.MARKET_INSIGHTS_REPORTS,
          analyticsEvent: analyticsDesktopHeaderEvents.marketInsightsReports,
          specificSite: [
            siteNames.employer.seek.hk,
            siteNames.employer.seek.id,
            siteNames.employer.seek.my,
            siteNames.employer.seek.ph,
            siteNames.employer.seek.sg,
            siteNames.employer.seek.th
          ]
        }
      ]
    }
  ];
  return {
    unauthenticatedNavItems: getFilteredHeaderNavLinks(
      unauthenticatedNavItems,
      site
    ),
    authenticatedNavItems: getFilteredHeaderNavLinks(
      authenticatedNavItems,
      site
    )
  };
};
const SITE_SKIP_TO_CONTENT = "start-of-content";
const EmployerHeader = ({
  identityContext: identity,
  user,
  advertiser,
  activeTabId,
  activeSubTabId,
  basic = false,
  error,
  linkRenderer: LinkRenderer = DefaultLinkRenderer,
  onToggleButtonClick = () => {
  },
  onTrackedItemInteraction = () => {
  },
  id,
  locale,
  site,
  isStaging,
  localisedContactInfo
}) => {
  const { t } = useTranslations(translations);
  const urlResolver = createUrlResolver({
    site,
    language: locale,
    staging: isStaging
  });
  const authenticationStatus = getAuthenticationStatus(identity);
  const { authenticatedNavItems, unauthenticatedNavItems } = useHeaderNavItems(
    site,
    urlResolver
  );
  const identityIsAuthenticated = isAuthenticated(authenticationStatus);
  const isAgency = Boolean(advertiser == null ? void 0 : advertiser.isAgency);
  const permissionOptions = (advertiser == null ? void 0 : advertiser.permissionOptions) ?? defaultPermissionOptions;
  const showOnCreditHoldMessage = Boolean(advertiser == null ? void 0 : advertiser.showOnCreditHoldMessage);
  const shouldDisplayDesktopNavigation = !basic && !isAgency;
  const shouldDisplayDesktopSubNavigation = !basic && !isAgency;
  const allNavItems = identityIsAuthenticated ? authenticatedNavItems : unauthenticatedNavItems;
  const navItems = allNavItems.filter(
    (item) => permissionOptions.canViewManagerReports || item.id !== "analytics"
  );
  const activeItem = navItems.find((item) => item.id === activeTabId);
  const activeSubItems = activeItem && activeItem.subItems ? activeItem.subItems : [];
  const handleItemInteraction = (event) => {
    onTrackedItemInteraction({
      ...event,
      accountPermissions: permissionOptions,
      authenticationStatus,
      isAgency,
      isSwitchAccountEnabled: Boolean(advertiser == null ? void 0 : advertiser.switchAccountsHref),
      isCreditOnHold: showOnCreditHoldMessage
    });
  };
  return /* @__PURE__ */ jsxs(
    EmployerHeaderProvider,
    {
      urlResolver,
      site,
      locale,
      identityContext: identity,
      localisedContactInfo,
      user,
      advertiser,
      basic,
      error,
      handleInteractionTracking: handleItemInteraction,
      children: [
        /* @__PURE__ */ jsx(ScreenReaderSkipLink, { to: SITE_SKIP_TO_CONTENT, children: t("Skip to content") }),
        showOnCreditHoldMessage && /* @__PURE__ */ jsx(CreditHoldAlert, { isStaging, locale, site }),
        (identity == null ? void 0 : identity.act) && /* @__PURE__ */ jsx(ImpersonationBanner, {}),
        /* @__PURE__ */ jsxs("header", { role: "banner", "aria-label": t("Site Header"), children: [
          /* @__PURE__ */ jsxs(
            Box,
            {
              position: "relative",
              display: "flex",
              alignItems: "center",
              paddingX: "small",
              width: "full",
              className: root,
              background: "customDark",
              flexWrap: "wrap",
              children: [
                /* @__PURE__ */ jsx(
                  Box,
                  {
                    display: "flex",
                    flexWrap: "wrap",
                    className: content,
                    paddingRight: "gutter",
                    children: /* @__PURE__ */ jsx(BrandedLogo, { linkRenderer: LinkRenderer })
                  }
                ),
                /* @__PURE__ */ jsxs(
                  Box,
                  {
                    display: "flex",
                    flexWrap: "wrap",
                    "flex-direction": "row-reverse",
                    flexGrow: 1,
                    children: [
                      shouldDisplayDesktopNavigation && /* @__PURE__ */ jsx(
                        DesktopNavigation,
                        {
                          items: navItems,
                          activeTabId,
                          linkRenderer: LinkRenderer
                        }
                      ),
                      /* @__PURE__ */ jsx(
                        Box,
                        {
                          display: "flex",
                          justifyContent: "flexEnd",
                          className: actionsContent,
                          children: /* @__PURE__ */ jsx(
                            HeaderActions,
                            {
                              id,
                              onToggleButtonClick,
                              linkRenderer: LinkRenderer,
                              site,
                              language: getLanguageFromLocale(locale),
                              identityIsAuthenticated
                            }
                          )
                        }
                      )
                    ]
                  }
                ),
                /* @__PURE__ */ jsx(
                  MobileMenuDropdown,
                  {
                    id,
                    activeSubTabId,
                    items: navItems,
                    onToggleButtonClick,
                    linkRenderer: LinkRenderer
                  }
                )
              ]
            }
          ),
          shouldDisplayDesktopSubNavigation && /* @__PURE__ */ jsx(
            DesktopSubNavigation,
            {
              activeSubTabId,
              subItems: activeSubItems,
              linkRenderer: LinkRenderer
            }
          )
        ] }),
        /* @__PURE__ */ jsx(ScreenReaderSkipTarget, { name: SITE_SKIP_TO_CONTENT })
      ]
    }
  );
};
export {
  EmployerHeader,
  testTags
};
